import {
  Text,
  Stack,
  Popover,
  PopoverContent,
  Link,
  InlineStack,
  Paragraph,
  Flex,
} from '@customerio/pluma-components/react';
import type { DeliverabilityMetricRow } from '../types';

interface DeliverabilityRatePopoverProps {
  isClick: boolean;
  row: DeliverabilityMetricRow;
  formattedPercent: string;
  formattedCount: string;
  color: 'inherit' | 'critical' | 'caution';
}

export function DeliverabilityRatePopover({
  isClick,
  row,
  formattedPercent,
  formattedCount,
  color,
}: DeliverabilityRatePopoverProps) {
  const popoverRows = isClick
    ? [
        { label: 'Clicked', rate: row.clickRate, count: row.clickCount },
        { label: 'Human Clicked', rate: row.humanClickRate, count: row.humanClickCount },
      ]
    : [
        { label: 'Opened', rate: row.openRate, count: row.openCount },
        { label: 'Human Opened', rate: row.humanOpenRate, count: row.humanOpenCount },
      ];

  return (
    <Popover placement="right">
      <Stack cursor="help">
        <Text color={color}>{formattedPercent}</Text>
        <Text color="subtle" size="xs">
          {formattedCount}
        </Text>
      </Stack>
      <PopoverContent style={{ minWidth: '320px' }}>
        <Paragraph text="product-h3" textTransform="capitalize" mb="100">
          {isClick ? 'Clicked' : 'Opened'}
        </Paragraph>
        <Flex direction="column" gap="050" alignItems="stretch" mb="100">
          {popoverRows.map((popoverRow) => (
            <InlineStack key={popoverRow.label} gap="500" alignBlock="center" alignInline="space-between">
              <Text size="s" textTransform="capitalize">
                {popoverRow.label}
              </Text>
              <InlineStack gap="050" alignBlock="center" alignInline="space-between">
                <Text color="subtle">
                  {popoverRow.count === 0 ? '—' : `${formatNumber(popoverRow.count)} of ${formattedCount}`}
                </Text>
                <Text style={{ minWidth: '5ch' }} textAlign="right" weight="semibold">
                  {popoverRow.count === 0 ? '—' : formatPercent(popoverRow.rate, 1)}
                </Text>
              </InlineStack>
            </InlineStack>
          ))}
        </Flex>
        <Link href="http://docs.customer.io/journeys/analytics/#delivery-metrics" isExternal>
          Learn about human {isClick ? 'clicks' : 'opens'}
        </Link>
      </PopoverContent>
    </Popover>
  );
}

function formatPercent(value: number, precision: number): string {
  const percent = value * 100;
  const threshold = Math.pow(10, -precision);

  if (percent > 0 && percent < threshold) {
    return `<${threshold.toFixed(precision)}%`;
  }

  return `${percent.toFixed(precision)}%`;
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}
